/**
 * Progressive Web App (PWA) Utilities
 * Handles installation, notifications, and offline functionality
 */

import { logger } from './Logger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
  getInstalledRelatedApps?: () => Promise<InstalledRelatedApp[]>;
}

interface InstalledRelatedApp {
  id?: string;
  platform?: string;
  url?: string;
  version?: string;
}

interface SyncCapableRegistration extends ServiceWorkerRegistration {
  sync: {
    register: (tag: string) => Promise<void>;
  };
}

interface PeriodicSyncCapableRegistration extends ServiceWorkerRegistration {
  periodicSync: {
    register: (tag: string, options: { minInterval: number }) => Promise<void>;
  };
}

function hasBackgroundSync(
  registration: ServiceWorkerRegistration
): registration is SyncCapableRegistration {
  return 'sync' in registration;
}

function hasPeriodicSync(
  registration: ServiceWorkerRegistration
): registration is PeriodicSyncCapableRegistration {
  return 'periodicSync' in registration;
}

// Service Worker registration
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    logger.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    logger.info('Service worker registered', { scope: registration.scope });

    // Check for updates every hour
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    logger.error('Service worker registration failed', error);
    return null;
  }
}

// PWA Install Prompt
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function setupInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Prevent the mini-infobar from appearing
    event.preventDefault();

    // Save the event for later use
    deferredPrompt = event as BeforeInstallPromptEvent;

    // Show your custom install button
    const installButton = document.getElementById('install-pwa-button');
    if (installButton) {
      installButton.style.display = 'block';
    }

    logger.info('PWA install prompt available');
  });

  // Handle successful installation
  window.addEventListener('appinstalled', () => {
    logger.info('PWA installed successfully');
    deferredPrompt = null;

    // Hide install button
    const installButton = document.getElementById('install-pwa-button');
    if (installButton) {
      installButton.style.display = 'none';
    }

    // Track installation (analytics)
    trackEvent('pwa_installed');
  });
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) {
    logger.info('No install prompt available');
    return false;
  }

  // Show the install prompt
  deferredPrompt.prompt();

  // Wait for the user's response
  const { outcome } = await deferredPrompt.userChoice;

  logger.info('Install prompt outcome', { outcome });

  // Clear the deferred prompt
  deferredPrompt = null;

  return outcome === 'accepted';
}

export function isPWAInstalled(): boolean {
  // Check if app is running in standalone mode
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as NavigatorWithStandalone).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

// Push Notifications
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    logger.warn('Notifications not supported');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  logger.info('Notification permission', { permission });

  if (permission === 'granted') {
    await subscribeToPushNotifications();
  }

  return permission;
}

export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;

  if (!registration.pushManager) {
    logger.warn('Push notifications not supported');
    return null;
  }

  try {
    // Get existing subscription or create new one
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Replace with your VAPID public key
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });
    }

    logger.info('Push subscription', { subscription });

    // Send subscription to your backend
    await sendSubscriptionToBackend(subscription);

    return subscription;
  } catch (error) {
    logger.error('Push subscription failed', error);
    return null;
  }
}

async function sendSubscriptionToBackend(subscription: PushSubscription) {
  try {
    const response = await fetch('/api/push-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
    });

    if (!response.ok) {
      throw new Error('Failed to send subscription');
    }

    logger.info('Subscription sent to backend');
  } catch (error) {
    logger.error('Error sending subscription', error);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Offline Detection
export function setupOfflineDetection() {
  window.addEventListener('online', () => {
    logger.info('App is online');
    showNotification('Back Online', 'Connection restored');

    // Trigger sync
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        if (hasBackgroundSync(registration)) {
          return registration.sync.register('sync-queue-actions');
        }

        return Promise.resolve();
      });
    }
  });

  window.addEventListener('offline', () => {
    logger.info('App is offline');
    showNotification('Offline Mode', 'Changes will sync when back online');
  });
}

export function isOnline(): boolean {
  return navigator.onLine;
}

// Show notification
export function showNotification(title: string, body: string, options?: NotificationOptions) {
  if (!('Notification' in window)) return;

  if (Notification.permission === 'granted') {
    navigator.serviceWorker.ready.then((registration) => {
      const notificationOptions: NotificationOptions = {
        body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-96x96.png',
        vibrate: [200, 100, 200],
        ...options,
      };
      registration.showNotification(title, notificationOptions);
    });
  }
}

// Background Sync
export async function registerBackgroundSync(tag: string) {
  if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (hasBackgroundSync(registration)) {
        await registration.sync.register(tag);
        logger.info('Background sync registered', { tag });
      }
    } catch (error) {
      logger.error('Background sync registration failed', error);
    }
  }
}

// Periodic Background Sync (for flight updates)
export async function registerPeriodicSync(tag: string, minInterval: number) {
  if ('serviceWorker' in navigator && 'periodicSync' in ServiceWorkerRegistration.prototype) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (hasPeriodicSync(registration)) {
        await registration.periodicSync.register(tag, {
          minInterval: minInterval, // in milliseconds
        });
        logger.info('Periodic sync registered', { tag });
      }
    } catch (error) {
      logger.error('Periodic sync registration failed', error);
    }
  }
}

// Analytics helper
function trackEvent(eventName: string, data?: unknown) {
  // Implement your analytics tracking here
  logger.info('Event tracked', { eventName, data });
}

// Share API
export async function shareContent(data: ShareData): Promise<boolean> {
  if (!('share' in navigator)) {
    logger.warn('Web Share API not supported');
    return false;
  }

  try {
    await navigator.share(data);
    logger.info('Content shared successfully');
    return true;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      logger.error('Share failed', error);
    }
    return false;
  }
}

// Get installed related apps
export async function getInstalledRelatedApps(): Promise<InstalledRelatedApp[]> {
  const navigatorWithApps = navigator as NavigatorWithStandalone;
  if (typeof navigatorWithApps.getInstalledRelatedApps === 'function') {
    const apps = await navigatorWithApps.getInstalledRelatedApps();
    logger.info('Installed related apps', { apps });
    return apps;
  }
  return [];
}

// Check if app can be installed
export function canInstallPWA(): boolean {
  return deferredPrompt !== null;
}
